import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
    CreateTileRequest,
    TileRepresentation,
    CreateDashboardRequest,
    DashboardRepresentation,
    TileDataSheetBackedWithSheet,
    TileGridPlacement,
} from '@common/api';
import { dashboardKeys } from './useDashboard';
import { dashboardsApi } from '@data/rest/api';
import { getMutationOptionsFactory, getCachedArrayResourceUpdater } from '@util/query';

import { FOLDERS_BY_PARENT_ID_KEY, invalidateFolderBranchQueries } from '../folder/folderActions';
import { useMemo } from 'react';

const mutationOptionsFactory = getMutationOptionsFactory();

const createDashboard = (dashboard: CreateDashboardRequest) => {
    return dashboardsApi.create(dashboard);
};

export const useCreateDashboard = () => {
    const key = dashboardKeys.all;
    const queryClient = useQueryClient();

    const options = mutationOptionsFactory.create<DashboardRepresentation>('create', key, queryClient, []);
    const onSuccess = options.onSuccess?.bind(options);
    options.onSuccess = (data, variable, context) => {
        if (onSuccess) {
            onSuccess(data, variable, context);
        }
        invalidateFolderBranchQueries();
        queryClient.invalidateQueries([FOLDERS_BY_PARENT_ID_KEY, variable.folderId]);
    };
    return useMutation(createDashboard, options).mutateAsync;
};

const updateDashboard = (updatedDashboard: DashboardRepresentation) => {
    const { id, ...rest } = updatedDashboard;
    return dashboardsApi.update(id, rest);
};

export const useUpdateDashboard = () => {
    const key = dashboardKeys.all;
    const queryClient = useQueryClient();

    const options = mutationOptionsFactory.create<DashboardRepresentation>('update', key, queryClient);
    const onSuccess = options.onSuccess?.bind(options);
    options.onSuccess = (data, variable, context) => {
        if (onSuccess) {
            onSuccess(data, variable, context);
        }
        invalidateFolderBranchQueries();
    };
    return useMutation(updateDashboard, options).mutateAsync;
};

const updatedDashboards = (updatedDashboards: DashboardRepresentation[]) => {
    return dashboardsApi.updateBatch(updatedDashboards);
};

export const useUpdateDashboards = () => {
    const queryClient = useQueryClient();

    const mutation = useMutation(updatedDashboards, {
        onMutate(data) {
            for (const dashboard of data) {
                queryClient.cancelQueries(dashboardKeys.dashboard(dashboard.id));
                queryClient.setQueryData(dashboardKeys.dashboard(dashboard.id), data);
            }
        },
        onSettled() {
            queryClient.invalidateQueries(dashboardKeys.dashboard());
            invalidateFolderBranchQueries();
        },
    });

    return useMemo(
        () => ({ updateDashboards: mutation.mutateAsync, isUpdating: mutation.isLoading }),
        [mutation.isLoading, mutation.mutateAsync]
    );
};

const duplicateDashboard = (command: { sourceId: number; folderId: number; name: string }) => {
    return dashboardsApi.duplicate(command);
};

export const useDuplicateDashboard = () => {
    const key = dashboardKeys.all;
    const queryClient = useQueryClient();

    return useMutation(duplicateDashboard, {
        async onSettled() {
            await queryClient.invalidateQueries(key);
            invalidateFolderBranchQueries();
        },
    }).mutateAsync;
};

type CreateTileArgs = { dashboardId: number; tile: CreateTileRequest };
const createTile = ({ dashboardId, tile }: CreateTileArgs) => {
    return dashboardsApi.createTile(dashboardId, tile);
};

export const useCreateTile = () => {
    const key = dashboardKeys.all;
    const queryClient = useQueryClient();

    return useMutation(createTile, {
        async onSettled() {
            await queryClient.invalidateQueries(key);
        },
    }).mutateAsync;
};

type UpdateTileArgs = { dashboardId: number; tile: TileRepresentation };
const updateTile = ({ dashboardId, tile }: UpdateTileArgs) => {
    const { id: tileId, ...rest } = tile;
    const sheetId = (rest.data as TileDataSheetBackedWithSheet).sheet?.id;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return dashboardsApi.updateTile(dashboardId, tileId, { ...rest, data: { ...rest.data, sheetId } as any });
};

export const useUpdateTile = () => {
    const key = dashboardKeys.all;
    const queryClient = useQueryClient();

    return useMutation(updateTile, {
        async onMutate({ dashboardId, tile }: UpdateTileArgs) {
            const queryKey = dashboardKeys.dashboard(dashboardId);
            const dashboard = queryClient.getQueryData<DashboardRepresentation>(queryKey);
            if (!dashboard) return;

            const updater = (dashboard?: DashboardRepresentation) =>
                dashboard && {
                    ...dashboard,
                    tiles: getCachedArrayResourceUpdater<TileRepresentation>(tile)(dashboard.tiles) ?? [],
                };

            queryClient.setQueryData(queryKey, updater);
        },
        async onSettled() {
            await queryClient.invalidateQueries(key);
        },
    }).mutateAsync;
};

type DeleteTileArgs = { dashboardId: number; tileId: number };
const deleteTile = ({ dashboardId, tileId }: DeleteTileArgs) => {
    return dashboardsApi.removeTile(dashboardId, tileId);
};

export const useDeleteTile = () => {
    const key = dashboardKeys.all;
    const queryClient = useQueryClient();

    return useMutation(deleteTile, {
        async onSettled() {
            await queryClient.invalidateQueries(key);
        },
    }).mutateAsync;
};

type DuplicateTileArgs = {
    sourceDashboardId: number;
    targetDashboardId?: number;
    name: string;
    sourceTileId: number;
    gridPlacements: Record<string, TileGridPlacement>;
};
const duplicateTile = ({
    sourceDashboardId,
    targetDashboardId,
    name,
    sourceTileId,
    gridPlacements,
}: DuplicateTileArgs) => {
    return dashboardsApi.duplicateTile(sourceDashboardId, { targetDashboardId, name, sourceTileId, gridPlacements });
};

export const useDuplicateTile = () => {
    const key = dashboardKeys.all;
    const queryClient = useQueryClient();

    return useMutation(duplicateTile, {
        async onSettled() {
            await queryClient.invalidateQueries(key);
        },
    }).mutateAsync;
};
